<template>
  <div v-if="report.length === 0 && loading === false">
    No data available
  </div>
  <div v-else-if="report.length === 0">
    <div :class="(index === stats.length - 1 && stats.length % 2) ? 'col-xl-12' : 'col-xl-6'" class="chartCol mt-4"
         v-for="(stat, index) in stats" :key="stat">
      <graph-skeleton class="mx-3"></graph-skeleton>
    </div>
  </div>
  <div v-else id="reportChart">
    <div
      :class="(index === Object.keys(graphs).length - 1 && Object.keys(graphs).length % 2) ? 'col-xl-12' : 'col-xl-6'"
      class="chartCol mt-4" v-for="(graph, name, index) in graphs" :key="name">
      <highcharts :options="graph" ref="highcharts"></highcharts>
    </div>
  </div>
</template>

<style scoped>
  .chartCol {
    display: inline-block;
  }

  #reportChart {
    overflow: hidden;
  }
</style>

<script>
    import {
        getPrettyStatName,
        getTimeZoneOffsetMillis,
        graphColors,
        formatStat,
        getFormattedDate,
        getEntryName
    } from '@/reports/components/ReportUtils'
    import {getTooltipHTML, getStyledGraphOptions} from '@/reports/components/graphs/ReportGraphUtils'
    import GraphSkeleton from '@/reports/components/graphs/GraphSkeleton'

    import moment from 'moment'

    window.moment = moment

    export default {
        props: {
            report: {required: true, type: Array},
            stats: {required: true, type: Array},
            fields: {required: true, type: Array},
            timezone: {required: false, type: String, default: 'UTC'},
            interval: {required: false, type: String},
            jointTooltip: {required: false, type: Boolean, default: false},
            currentlyViewedReport: {required: false, type: Array},
            loading: {type: Boolean, default: true}
        },
        watch: {
            currentlyViewedReport: {
                handler: function () {
                    this.$nextTick(function () {
                        for (let row of this.currentlyViewedReport) {
                            let name = getEntryName(row, this.fields)
                            if (row.disabled) {
                                this.hideSeries(name)
                            } else {
                                this.showSeries(name)
                            }
                        }
                    })
                },
                deep: true
            }
        },
        data() {
            return {}
        },
        updated: function () {
            this.beginChartSynchronization()
        },
        mounted: function () {
            this.$nextTick(function () {
                this.beginChartSynchronization()
            })
        },
        methods: {
            //TODO only supports up to 20 colors
            getTimeZoneOffsetMillis,
            //convert report into the format we need
            getMultipleSeries() {
                let nameToColor = {}
                let colorOn = 0
                let graphs = {}
                //get the field that isn't date
                let relevantField = this.fields[this.fields.length - 1]
                for (let stat of this.stats) {
                    graphs[stat] = {}
                    for (let row of this.report) {
                        let name = row[relevantField]
                        if (!this.currentlyViewedReport || this.currentlyViewedReport.find(row => row[relevantField] === name)) {
                            name = getEntryName(row, [relevantField])
                            if (!(name in nameToColor)) {
                                nameToColor[String(name)] = graphColors[(colorOn++ % graphColors.length)]
                            }
                            if (!graphs[stat][name]) {
                                graphs[stat][name] = []
                            }
                            let datenum = Number(row.date)
                            graphs[stat][name].push([datenum, Number(row[stat])])
                        }
                    }
                }

                /* highcharts needs sorted by timestamp it seems or charts get hella ugly */
                for (let stat of this.stats) {
                    for (let row of this.report) {
                        let name = row[relevantField]
                        let graph = graphs[stat][name]
                        if (!graph) {
                            continue
                        }
                        graph.sort((a, b) => a[0] - b[0])
                    }
                }

                this.$emit('color-update', nameToColor)
                //now we have all the data formatted, it's just not formatted right
                for (let stat in graphs) {
                    let arr = []
                    for (let field in graphs[stat]) {
                        arr.push({name: field, data: graphs[stat][field], color: nameToColor[field]})
                    }
                    graphs[stat] = arr
                }
                //make it so that ie mousehovers on on chart also affect the others
                return graphs
            },
            beginChartSynchronization() {
                //if we're sharing tooltips in a single chart we don't want to sync with other charts
                if (!this.jointTooltip) {
                    //override some highcharts methods so that we can use our own
                    this.overrideHighcharts()
                    //override event listeners
                    let element = document.getElementById('reportChart')
                    element.addEventListener('mousemove', this.handleMouseMove)
                }
            },
            overrideHighcharts() {
                /**
                 * Highlight a point by showing tooltip, setting hover state and draw crosshair
                 */
                this.Highcharts.Point.prototype.highlight = function (event) {
                    this.onMouseOver(); // Show the hover marker
                    this.series.chart.tooltip.refresh(this); // Show the tooltip
                    this.series.chart.xAxis[0].drawCrosshair(event, this); // Show the crosshair
                };
            },
            //returns true if the (normalized) event takes place within the chart
            eventInChart(event, chart) {
                return event.chartX > 0 && event.chartX <= chart.chartWidth && event.chartY > 0 && event.chartY <= chart.chartHeight
            },
            //get all charts that are truthy
            getValidCharts() {
                let list = this.Highcharts.charts.filter(chart => chart)
                return list
            },
            getVisibleSeries(chart) {
                return chart.series.filter(series => series.visible)
            },
            handleMouseMove(e) {
                let chart,
                    point,
                    i,
                    event;
                this.getValidCharts()
                for (let chart of this.getValidCharts()) {
                    event = chart.pointer.normalize(e); // Find coordinates within the chart
                    if (this.eventInChart(event, chart)) {
                        point = chart.pointer.findNearestKDPoint(this.getVisibleSeries(chart), false, event)
                        if (point) {
                            //point.highlight(e);
                            this.syncPoints(point, event)
                        }
                    }
                }
            },
            //given a point, highlight equivalent point on the same series in every chart on the page
            syncPoints(point, event) {
                let seriesName = point.series.name
                for (let chart of this.getValidCharts()) {
                    let series = chart.series.find(series => series.name === seriesName)
                    point = series.points.find(tempPoint => tempPoint.x === point.x)
                    if (point) {
                        point.highlight(event)
                    }
                }
            },
            hideSeries(name) {
                for (let chart of this.getValidCharts()) {
                    let seriesList = chart.series.filter(series => series.name === name)
                    for (let series of seriesList) {
                        if (series.visible) {
                            series.hide()
                        }
                    }
                }
            },
            showSeries(name) {
                for (let chart of this.getValidCharts()) {
                    let seriesList = chart.series.filter(series => series.name === name)
                    for (let series of seriesList) {
                        if (!series.visible) {
                            series.show()
                        }
                    }
                }
            },
            /**
             * Synchronize zooming through the setExtremes event handler.
             */
            syncExtremes(e) {
                var thisChart = this.chart;
                if (e.trigger !== 'syncExtremes') { // Prevent feedback loop
                    this.Highcharts.each(this.getValidCharts(), function (chart) {
                        if (chart !== thisChart) {
                            if (chart.xAxis[0].setExtremes) { // It is null while updating
                                chart.xAxis[0].setExtremes(
                                    e.min,
                                    e.max,
                                    undefined,
                                    false,
                                    {trigger: 'syncExtremes'}
                                );
                            }
                        }
                    });
                }
            },
            getFormattedTooltipDate(date) {
                return getFormattedDate(date, this.interval, this.timezone)
            },
        },
        computed: {
            /* this method does two different things, depending on the fields
            if there's one field (ie, the only thing being selected by is date), it'll make a series for a graph where each line is a stat
            if there are two fields, it'll make multiple series, one series for each stat, where each line is one of whatever else is being selected for
            if there are more than two fields, it won't return anything cuz that behavior is undefined
            */
            computedSeries() {
                return this.getMultipleSeries()
            },
            graphs() {
                let self = this
                let graphs = {}
                for (let graph in this.computedSeries) {
                    let options = {
                        chart: {
                            zoomType: 'x',
                            backgroundColor: 'white',
                            type: 'spline',
                            height: 300,
                            style: {
                                fontFamily: 'Gilroy, Helvetica, sans-serif',
                            }
                        },
                        colors: graphColors,
                        title: {
                            align: 'left',
                            text: getPrettyStatName(graph),
                            margin: 30,
                            style: {
                                fontSize: '20px',
                                fontWeight: 500,
                                color: '#21262c'
                            }
                        },
                        xAxis: {
                            offset: 10,
                            type: 'datetime',
                            dateTimeLabelFormats: {
                                millisecond: '%H:%M:%S.%L',
                                second: '%H:%M:%S',
                                minute: '%H:%M',
                                hour: '%H:%M',
                                day: '%b %e',
                                week: '%b %e',
                                month: '%b \'%y',
                                year: '%Y'
                            },
                            events: {
                                setExtremes: this.syncExtremes
                            }
                        },
                        plotOptions: {
                            spline: {
                                marker: {
                                    enabled: false,
                                    lineColor: null,
                                    symbol: 'circle',
                                    radius: 3
                                },
                                states: {
                                    hover: {
                                        halo: false
                                    }
                                }
                            },
                            series: {
                                events: {
                                    hide: function (e) {
                                        self.hideSeries(e.target.name)
                                    },
                                    show: function (e) {
                                        self.showSeries(e.target.name)
                                    }
                                }
                            }
                        },
                        time: {
                            timezone: this.timezone
                        },
                        yAxis: {
                            title: '',
                            labels: {
                                formatter: function () {
                                    return formatStat(graph, this.value)
                                }
                            }
                        },
                        legend: {
                            enabled: false
                        },
                        series: this.computedSeries[graph],
                        credits: {
                            enabled: false
                        },
                        tooltip: {
                            useHTML: true,
                            borderColor: '#1C1D29',
                            backgroundColor: '#1C1D29',
                            borderRadius: 5,
                            followPointer: false,
                            shared: this.jointTooltip,
                            formatter: function () {
                                let title
                                if (self.interval) {
                                    //make sure it works with all series hidden
                                    title = self.getFormattedTooltipDate(this.x)
                                } else {
                                    title = "Today's Statistics"
                                }
                                if (self.jointTooltip) {
                                    let points = []
                                    for (let point of this.points) {
                                        points.push({
                                            name: point.series.name,
                                            color: point.color,
                                            value: formatStat(graph, point.y)
                                        })
                                    }
                                    return getTooltipHTML(title, points)
                                } else {
                                    return getTooltipHTML(title, [{
                                        name: this.point.series.name,
                                        color: this.point.color,
                                        value: formatStat(graph, this.point.y)
                                    }])
                                }
                            }
                        }
                    }
                    graphs[graph] = _.merge(options, getStyledGraphOptions())
                }
                return graphs
            }
        },
        components: {
            GraphSkeleton
        }
    }
</script>



// WEBPACK FOOTER //
// src/reports/components/graphs/ReportTimeStatGraphs.vue